import { AxiosResponse } from "axios";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { REQUEST, FAILURE, SUCCESS } from "./util/action-types";
import {
	getAccount,
	authenticate,
	logout as userLogout
} from "shared/network/user-api";
import { AUTH_TOKEN_KEY } from "config/constants";
import User from "shared/types/user.model";
import { IRootState } from ".";

export const ACTION_TYPES = {
	LOGIN: "authentication/LOGIN",
	GET_SESSION: "authentication/GET_SESSION",
	LOGOUT: "authentication/LOGOUT",
	CLEAR_AUTH: "authentication/CLEAR_AUTH",
	ERROR_MESSAGE: "authentication/ERROR_MESSAGE",
	CLEAR: "authentication/CLEAR"
};

const initialState = {
	loading: false,
	isAuthenticated: false,
	loginSuccess: false,
	loginError: false,
	fetchSessionError: false,
	sessionFetched: false,
	account: {} as User,
	errorMessage: (null as unknown) as any
};

export type AuthenticationState = Readonly<typeof initialState>;

export default (
	state: AuthenticationState = initialState,
	action: any
): AuthenticationState => {
	switch (action.type) {
		case REQUEST(ACTION_TYPES.LOGIN):
		case REQUEST(ACTION_TYPES.GET_SESSION):
			return {
				...state,
				loading: true
			};
		case FAILURE(ACTION_TYPES.LOGIN):
			return {
				...initialState,
				loading: false,
				loginError: true,
				errorMessage: action?.payload?.data
			};
		case FAILURE(ACTION_TYPES.GET_SESSION):
			return {
				...state,
				loading: false,
				isAuthenticated: false,
				fetchSessionError: true,
				errorMessage: action?.payload?.data
			};
		case SUCCESS(ACTION_TYPES.LOGIN):
			return {
				...state,
				loading: false,
				loginSuccess: true
			};
		case SUCCESS(ACTION_TYPES.GET_SESSION): {
			const { user }: { user: User } = action.payload.data;
			const isAuthenticated = user.status === "ACTIVE";
			return {
				...state,
				loading: false,
				isAuthenticated,
				sessionFetched: true,
				account: user
			};
		}
		case ACTION_TYPES.ERROR_MESSAGE:
			return {
				...initialState
			};
		case ACTION_TYPES.CLEAR_AUTH:
			return {
				...state,
				loading: false,
				isAuthenticated: false
			};
		case ACTION_TYPES.LOGOUT:
		case ACTION_TYPES.CLEAR:
			return initialState;
		default:
			return state;
	}
};

export const login = (
	username: string,
	password: string,
	rememberMe = false
): ThunkAction<
	void,
	IRootState,
	AxiosResponse,
	Action<string>
> => async dispatch => {
	const result = await dispatch({
		type: ACTION_TYPES.LOGIN,
		payload: authenticate(username, password, rememberMe)
	});

	// @ts-ignore
	const jwtToken = result?.value?.data?.token;

	if (jwtToken) {
		if (rememberMe) {
			localStorage.setItem(AUTH_TOKEN_KEY, jwtToken);
		} else {
			sessionStorage.setItem(AUTH_TOKEN_KEY, jwtToken);
		}
	}

	// @ts-ignore
	await dispatch(getSession());
};
export const logout = (): ThunkAction<
	void,
	IRootState,
	AxiosResponse,
	Action<string>
> => async dispatch => {
	let token = "";

	if (localStorage.getItem(AUTH_TOKEN_KEY)) {
		token = localStorage.getItem(AUTH_TOKEN_KEY) ?? "";
	} else {
		token = sessionStorage.getItem(AUTH_TOKEN_KEY) ?? "";
	}
	userLogout(token);
	clearAuthToken();
	await dispatch({ type: ACTION_TYPES.LOGOUT });
};

export const clearAuthToken = () => {
	if (localStorage.getItem(AUTH_TOKEN_KEY) !== null) {
		localStorage.removeItem(AUTH_TOKEN_KEY);
	}
	if (sessionStorage.getItem(AUTH_TOKEN_KEY) !== null) {
		sessionStorage.removeItem(AUTH_TOKEN_KEY);
	}
};

export const getSession = (): ThunkAction<
	void,
	IRootState,
	null,
	Action<string>
> => async dispatch => {
	await dispatch({
		type: ACTION_TYPES.GET_SESSION,
		payload: getAccount()
	});
};

export const clearAuthentication = (): ThunkAction<
	void,
	IRootState,
	null,
	Action<string>
> => dispatch => {
	clearAuthToken();
	dispatch({
		type: ACTION_TYPES.CLEAR
	});
};
